/**
 * ServiceFeeCard — inline pay-at-booking for retail services whose
 * paymentMode is "request_with_skip" or "require" (Manny #148).
 *
 * Distinct from DepositCard which handles the new-wig-consultation
 * $150 deposit. ServiceFeeCard collects the full service fee upfront
 * (or just the high estimate of a price range) so the customer can
 * pay before they arrive.
 *
 * Skip toggle is only available when ALL selected services are
 * "request_with_skip". A single "require" service forces the card.
 *
 * Renders nothing when there are no selected services that demand
 * a card form.
 */
"use client";

import { useState, useEffect } from "react";
import { CreditCard } from "lucide-react";
import { useTranslations } from "next-intl";
import { SquareCardForm } from "@/components/checkout/SquareCardForm";

export interface PricedService {
  /** SERVICES.id (wash_set, restyle, ...) */
  id: string;
  /** Resolved price for this service — uses priceMax when set */
  amount: number;
  /** "request_with_skip" or "require" */
  paymentMode: "request_with_skip" | "require";
  /** Customer-facing label, e.g. "Wash & Set" */
  label: string;
}

interface ServiceFeeCardProps {
  services: PricedService[];
  submitting: boolean;
  /**
   * Called when the customer pays — sourceId is the Square nonce.
   * Parent forwards to /api/book in the same submit.
   */
  onPay: (sourceId: string, totalDollars: number) => Promise<void>;
  /**
   * Called when the customer chooses skip — parent should call its
   * normal submit handler with no source.
   */
  onSkip: () => void;
  onPaymentError: (msg: string) => void;
  setPaying: (v: boolean) => void;
  paying: boolean;
}

export default function ServiceFeeCard({
  services,
  submitting,
  onPay,
  onSkip,
  onPaymentError,
  setPaying,
  paying,
}: ServiceFeeCardProps) {
  const t = useTranslations("booking");
  const total = services.reduce((acc, s) => acc + s.amount, 0);
  const canSkip = services.every((s) => s.paymentMode === "request_with_skip");
  const [skip, setSkip] = useState(false);
  // If "require" appears in the mix, force skip = false.
  useEffect(() => {
    if (!canSkip && skip) setSkip(false);
  }, [canSkip, skip]);

  if (services.length === 0 || total <= 0) return null;

  return (
    <div className="mt-6 rounded-xl border border-brand-gold/30 bg-brand-cream/30 p-5">
      <div className="flex items-start gap-3 pb-4 mb-4 border-b border-brand-gold/20">
        <CreditCard className="h-5 w-5 text-brand-gold flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-dark">
            {t("serviceFeeAmount", { amount: `$${total.toFixed(2)}` })}
          </p>
          <p className="text-xs text-brand-muted mt-0.5">
            {services.length === 1
              ? t("serviceFeeForOne", { label: services[0].label })
              : t("serviceFeeForMany", {
                  count: services.length,
                  list: services.map((s) => s.label).join(", "),
                })}
            {" "}{t("serviceFeeCaveat")}
          </p>
        </div>
      </div>

      {canSkip && (
        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={skip}
            onChange={(e) => setSkip(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
          />
          <span className="text-xs text-brand-dark">
            <span className="font-medium">{t("serviceFeeSkipLabel")}</span>
            <span className="block text-brand-muted mt-0.5">
              {t("serviceFeeSkipHint")}
            </span>
          </span>
        </label>
      )}

      {skip ? (
        <button
          type="button"
          onClick={onSkip}
          disabled={submitting}
          className="w-full rounded-lg bg-brand-dark text-white py-3 text-sm font-medium hover:bg-brand-gold disabled:opacity-60 transition-colors"
        >
          {t("serviceFeeBookWithoutPaying")}
        </button>
      ) : (
        <SquareCardForm
          amount={total}
          isProcessing={paying || submitting}
          setIsProcessing={setPaying}
          onPaymentSuccess={(sourceId) => onPay(sourceId, total)}
          onPaymentError={onPaymentError}
          buttonLabel={t("serviceFeePayAndBook", { amount: `$${total.toFixed(2)}` })}
          processingLabel={t("serviceFeeBooking")}
          walletLabel={t("serviceFeeWalletLabel")}
        />
      )}
    </div>
  );
}
